import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Box,
  Typography,
  Alert
} from '@mui/material';
import { Close } from '@mui/icons-material';
import CalendarService from '../../services/calendar.service';
import ClientService from '../../services/client.service';
import { useTranslation } from '../../hooks/useTranslation';

const normalizeClientId = (id) => {
  if (id == null || id === '') return '';
  const n = Number(id);
  return Number.isFinite(n) && n > 0 ? n : '';
};

const AppointmentDialog = ({
  open,
  onClose,
  selectedSlot,
  selectedDate,
  initialClientId,
  onAppointmentCreated
}) => {
  const { t } = useTranslation();
  const [clients, setClients] = useState([]);
  const [formData, setFormData] = useState({
    client_id: normalizeClientId(initialClientId),
    date: selectedDate || '',
    start_time: '',
    end_time: '',
    visit_id: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;

    loadClients();

    const clientId = normalizeClientId(initialClientId);
    setFormData((prev) => {
      const next = {
        ...prev,
        client_id: clientId || prev.client_id,
        date: selectedDate || prev.date,
      };
      if (selectedSlot && selectedDate) {
        next.date = selectedDate;
        next.start_time = selectedSlot.start_time;
        next.end_time = selectedSlot.end_time;
      }
      return next;
    });
  }, [open, selectedSlot, selectedDate, initialClientId]);

  useEffect(() => {
    if (!open || !initialClientId || !clients.length) return;
    const clientId = normalizeClientId(initialClientId);
    const exists = clients.some((c) => Number(c.id) === clientId);
    if (exists) {
      setFormData((prev) => ({ ...prev, client_id: clientId }));
    }
  }, [open, initialClientId, clients]);

  const loadClients = async () => {
    try {
      const response = await ClientService.getClients();
      const list = response.data?.data ?? response.data ?? [];
      setClients(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('Error loading clients:', error);
      setClients([]);
    }
  };

  const handleSubmit = async () => {
    if (!formData.client_id || !formData.date || !formData.start_time || !formData.end_time) {
      setError(t('calendar.dialog.errors.fillRequiredFields'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      await CalendarService.createAppointment(formData);
      onAppointmentCreated();
      onClose();
      setFormData({
        client_id: '',
        date: '',
        start_time: '',
        end_time: '',
        visit_id: ''
      });
    } catch (error) {
      setError(error.response?.data?.message || t('calendar.dialog.errors.errorCreatingAppointment'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      client_id: '',
      date: '',
      start_time: '',
      end_time: '',
      visit_id: ''
    });
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">{t('calendar.dialog.scheduleNewAppointmentTitle')}</Typography>
          <Button onClick={handleClose} sx={{ minWidth: 'auto', p: 1 }}>
            <Close />
          </Button>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>{t('calendar.dialog.clientRequired')}</InputLabel>
                <Select
                  value={formData.client_id === '' ? '' : Number(formData.client_id)}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      client_id: normalizeClientId(e.target.value),
                    }))
                  }
                  label={t('calendar.dialog.clientRequired')}
                >
                  {clients.map((client) => (
                    <MenuItem key={client.id} value={Number(client.id)}>
                      {client.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label={t('calendar.dialog.dateRequired')}
                type="date"
                value={formData.date}
                onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label={t('calendar.dialog.startTimeRequired')}
                type="time"
                value={formData.start_time}
                onChange={(e) => setFormData(prev => ({ ...prev, start_time: e.target.value }))}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label={t('calendar.dialog.endTimeRequired')}
                type="time"
                value={formData.end_time}
                onChange={(e) => setFormData(prev => ({ ...prev, end_time: e.target.value }))}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label={t('calendar.dialog.visitIdOptional')}
                value={formData.visit_id}
                onChange={(e) => setFormData(prev => ({ ...prev, visit_id: e.target.value }))}
                fullWidth
                placeholder={t('calendar.dialog.visitLinkPlaceholder')}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {t('calendar.form.cancel')}
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading}
          sx={{ bgcolor: 'blue.600', '&:hover': { bgcolor: 'blue.700' } }}
        >
          {loading ? t('calendar.form.creating') : t('calendar.dialog.createAppointment')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AppointmentDialog;
